import { useState, useEffect, useRef } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import axios from "axios";
import { toast } from "sonner";
import { Image as ImageIcon, X, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyClient = {
  name: "",
  client_code: "",
  legal_name: "",
  desconto: "",
  email: "",
  contacts: [{ name: "", cargo: "", phone: "" }],
  website: "",
  social_media: "",
  address: "",
  postal_code: "",
  gps_coordinates: "",
  city: "",
  country: "",
  notes: "",
  perfil_interno: "",
  logo_url: "",
  parent_client_id: "",
  primary_branch_id: ""
};

const emptyBranch = { name: "", address: "", postal_code: "", city: "", country: "", gps_coordinates: "", contact_person: "", phone: "", email: "" };

// Reusable client create/edit dialog - used by Clients.jsx (grid) and ClientDetail.jsx (per-branch edit)
export default function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const [formData, setFormData] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const logoInputRef = useRef(null);

  // Sucursais (branches of the client being edited)
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [showAddBranch, setShowAddBranch] = useState(false);
  const [newBranch, setNewBranch] = useState(emptyBranch);
  const [savingBranch, setSavingBranch] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [editBranchForm, setEditBranchForm] = useState(null);

  const fetchBranches = async () => {
    if (!client) return;
    setLoadingBranches(true);
    try {
      const res = await axios.get(`${API}/clients/${client.id}/branches`);
      setBranches((res.data || []).filter(b => b.id !== client.id));
    } catch (error) {
      console.error("Error loading branches:", error);
    } finally {
      setLoadingBranches(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (client) {
      setFormData({
        name: client.name || "",
        client_code: client.client_code || "",
        legal_name: client.legal_name || "",
        desconto: client.desconto || "",
        email: client.email || "",
        contacts: (client.contacts && client.contacts.length) ? client.contacts : [{ name: "", cargo: "", phone: "" }],
        website: client.website || "",
        social_media: client.social_media || "",
        address: client.address || "",
        postal_code: client.postal_code || "",
        gps_coordinates: client.gps_coordinates || "",
        city: client.city || "",
        country: client.country || "",
        notes: client.notes || "",
        perfil_interno: client.perfil_interno || "",
        logo_url: client.logo_url || "",
        parent_client_id: client.parent_client_id || "",
        primary_branch_id: client.primary_branch_id || ""
      });
      const logoUrl = client.logo_url || null;
      setLogoPreview(logoUrl && logoUrl.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : logoUrl);
      fetchBranches();
    } else {
      setFormData(emptyClient);
      setLogoPreview(null);
      setBranches([]);
    }
    setPendingLogoFile(null);
    setShowAddBranch(false);
    setNewBranch(emptyBranch);
    setEditingBranchId(null);
  }, [open, client]);

  const addBranch = async () => {
    if (!newBranch.name.trim()) {
      toast.error("O nome da sucursal é obrigatório");
      return;
    }
    setSavingBranch(true);
    try {
      await axios.post(`${API}/clients`, { ...emptyClient, ...newBranch, parent_client_id: client.id });
      toast.success("Sucursal adicionada");
      setNewBranch(emptyBranch);
      setShowAddBranch(false);
      await fetchBranches();
      onSaved?.(client.id);
    } catch (error) {
      toast.error("Erro ao adicionar sucursal");
    } finally {
      setSavingBranch(false);
    }
  };

  const startEditBranch = async (branch) => {
    try {
      const res = await axios.get(`${API}/clients/${branch.id}`);
      setEditingBranchId(branch.id);
      setEditBranchForm({
        name: res.data.name || "",
        address: res.data.address || "",
        postal_code: res.data.postal_code || "",
        city: res.data.city || "",
        country: res.data.country || "",
        gps_coordinates: res.data.gps_coordinates || "",
        contact_person: res.data.contact_person || "",
        phone: res.data.phone || "",
        email: res.data.email || ""
      });
    } catch (error) {
      toast.error("Erro ao carregar sucursal");
    }
  };

  const saveBranchEdit = async () => {
    setSavingBranch(true);
    try {
      await axios.put(`${API}/clients/${editingBranchId}`, editBranchForm);
      toast.success("Sucursal atualizada");
      setEditingBranchId(null);
      await fetchBranches();
    } catch (error) {
      toast.error("Erro ao guardar sucursal");
    } finally {
      setSavingBranch(false);
    }
  };

  const deleteBranch = async (branchId) => {
    if (!window.confirm("Eliminar esta sucursal?")) return;
    try {
      await axios.delete(`${API}/clients/${branchId}`);
      toast.success("Sucursal eliminada");
      await fetchBranches();
    } catch (error) {
      toast.error("Erro ao eliminar sucursal");
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      if (client) {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(`${API}/clients/${client.id}/logo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const logoUrl = response.data.logo_url;
        setFormData(prev => ({ ...prev, logo_url: logoUrl }));
        setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`);
      } else {
        setPendingLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
      }
      toast.success("Logo carregado");
    } catch (error) {
      toast.error("Erro ao carregar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: "" }));
    setLogoPreview(null);
    setPendingLogoFile(null);
  };

  const updateContact = (idx, field, value) => {
    setFormData(prev => {
      const contacts = [...prev.contacts];
      contacts[idx] = { ...contacts[idx], [field]: value };
      return { ...prev, contacts };
    });
  };

  const addContact = () => {
    setFormData(prev => ({ ...prev, contacts: [...prev.contacts, { name: "", cargo: "", phone: "" }] }));
  };

  const removeContact = (idx) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("O nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, contacts: formData.contacts.filter(c => c.name && c.name.trim()) };
      let savedId = client?.id;
      if (client) {
        await axios.put(`${API}/clients/${client.id}`, payload);
        toast.success("Cliente atualizado");
      } else {
        const response = await axios.post(`${API}/clients`, payload);
        savedId = response.data.id;
        if (pendingLogoFile && savedId) {
          const fd = new FormData();
          fd.append('file', pendingLogoFile);
          await axios.post(`${API}/clients/${savedId}/logo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success("Cliente criado");
      }
      onOpenChange(false);
      setPendingLogoFile(null);
      onSaved?.(savedId);
    } catch (error) {
      toast.error("Erro ao guardar cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Manrope]">
            {client ? "Editar Cliente" : "Novo Cliente"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              {logoPreview ? (
                <div className="relative w-16 h-16">
                  <img src={logoPreview} alt="Logo" className="w-16 h-16 object-contain border border-stone-200 rounded-lg bg-white" />
                  <button type="button" onClick={removeLogo} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => logoInputRef.current?.click()}
                  className="w-16 h-16 border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#00A19A] hover:bg-teal-50/30 transition-colors"
                >
                  {uploadingLogo ? (
                    <LoadingSpinner size={20} />
                  ) : (
                    <ImageIcon size={20} className="text-stone-400" />
                  )}
                </div>
              )}
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files[0])} />
            </div>

            <div className="flex gap-3">
              <div className="w-36 flex-shrink-0">
                <Label htmlFor="cf-client_code">Código</Label>
                <Input id="cf-client_code" value={formData.client_code} onChange={(e) => setFormData({ ...formData, client_code: e.target.value })} className="gresart-input mt-1" placeholder="1023456789" maxLength={10} data-testid="input-client-code" />
              </div>
              <div className="flex-1">
                <Label htmlFor="cf-legal_name">Cliente</Label>
                <Input id="cf-legal_name" value={formData.legal_name} onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })} className="gresart-input mt-1" placeholder="Nome completo conforme documentos financeiros" data-testid="input-legal-name" />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="cf-name">Nome Comercial *</Label>
                <Input id="cf-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="gresart-input mt-1" placeholder="Nome usado pelo cliente no seu negócio" data-testid="input-client-name" />
              </div>
              <div className="w-32 flex-shrink-0">
                <Label htmlFor="cf-desconto">Desconto</Label>
                <Input id="cf-desconto" value={formData.desconto} onChange={(e) => setFormData({ ...formData, desconto: e.target.value })} className="gresart-input mt-1" data-testid="input-desconto" />
              </div>
            </div>
            <div>
              <Label htmlFor="cf-address">Endereço</Label>
              <Input id="cf-address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="gresart-input mt-1" placeholder="Rua, número..." data-testid="input-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cf-postal_code">Código Postal</Label>
                <Input id="cf-postal_code" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="gresart-input mt-1" placeholder="0000-000" data-testid="input-postal-code" />
              </div>
              <div>
                <Label htmlFor="cf-city">Localidade</Label>
                <Input id="cf-city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="gresart-input mt-1" data-testid="input-city" />
              </div>
            </div>

            {/* Pessoas de Contacto - full width, right after Nome/Endereço/Codigo Postal/Localidade */}
            <div>
              <Label>Pessoas de Contacto</Label>
              <div className="space-y-3 mt-1">
                {formData.contacts.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-[2fr_1.4fr_1.4fr_auto] gap-3 items-end">
                    <div>
                      <Label htmlFor={`cf-contact-name-${idx}`}>Nome</Label>
                      <Input id={`cf-contact-name-${idx}`} placeholder="Nome" value={c.name} onChange={(e) => updateContact(idx, 'name', e.target.value)} className="gresart-input mt-1" data-testid={`input-contact-name-${idx}`} />
                    </div>
                    <div>
                      <Label htmlFor={`cf-contact-cargo-${idx}`}>Cargo</Label>
                      <Input id={`cf-contact-cargo-${idx}`} placeholder="Cargo" value={c.cargo || ""} onChange={(e) => updateContact(idx, 'cargo', e.target.value)} className="gresart-input mt-1" data-testid={`input-contact-cargo-${idx}`} />
                    </div>
                    <div>
                      <Label htmlFor={`cf-contact-phone-${idx}`}>Telefone</Label>
                      <Input id={`cf-contact-phone-${idx}`} placeholder="Telefone" value={c.phone || ""} onChange={(e) => updateContact(idx, 'phone', e.target.value)} className="gresart-input mt-1" data-testid={`input-contact-phone-${idx}`} />
                    </div>
                    <Button
                      type="button" variant="ghost" size="icon"
                      className="text-stone-400 hover:text-red-600 disabled:opacity-30"
                      disabled={formData.contacts.length <= 1}
                      onClick={() => removeContact(idx)}
                      data-testid={`btn-remove-contact-${idx}`}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addContact} data-testid="btn-add-contact">
                <Plus size={14} className="mr-1" /> Adicionar Pessoa de Contacto
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cf-country">País</Label>
                    <Input id="cf-country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="gresart-input mt-1" data-testid="input-country" />
                  </div>
                  <div>
                    <Label htmlFor="cf-gps_coordinates">Coordenadas GPS</Label>
                    <Input id="cf-gps_coordinates" value={formData.gps_coordinates} onChange={(e) => setFormData({ ...formData, gps_coordinates: e.target.value })} className="gresart-input mt-1" placeholder="41.1579, -8.6291" data-testid="input-gps-coordinates" />
                  </div>
                </div>
              </div>

              {/* Right Column - Contact Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cf-email">Email</Label>
                  <Input id="cf-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="gresart-input mt-1" placeholder="cliente@empresa.com" data-testid="input-email" />
                </div>
              </div>
            </div>

            {/* Web & Redes Sociais - full width of the edit screen, split evenly in half */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cf-website">Web</Label>
                <Input id="cf-website" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="gresart-input mt-1" placeholder="www.empresa.com" data-testid="input-website" />
              </div>
              <div>
                <Label htmlFor="cf-social_media">Redes Sociais</Label>
                <Input id="cf-social_media" value={formData.social_media} onChange={(e) => setFormData({ ...formData, social_media: e.target.value })} className="gresart-input mt-1" placeholder="@instagram, Facebook, etc." data-testid="input-social-media" />
              </div>
            </div>

            {/* Notas internas - full width */}
            <div>
              <Label htmlFor="cf-perfil_interno">Notas</Label>
              <Textarea id="cf-perfil_interno" value={formData.perfil_interno} onChange={(e) => setFormData({ ...formData, perfil_interno: e.target.value })} className="gresart-input mt-1 resize-y" data-testid="input-perfil-interno" />
            </div>
          </div>

          {client && (
            <div className="border border-stone-200 rounded-lg p-3 bg-stone-50/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0">Sucursais desta empresa</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAddBranch(v => !v)} data-testid="btn-toggle-add-branch">
                  <Plus size={14} className="mr-1" /> Adicionar Sucursal
                </Button>
              </div>

              {showAddBranch && (
                <div className="p-3 mb-2 rounded-lg bg-white border border-stone-200 space-y-2" data-testid="add-branch-form">
                  <Input placeholder="Nome da sucursal *" value={newBranch.name} onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })} data-testid="input-branch-name" />
                  <Input placeholder="Endereço" value={newBranch.address} onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })} data-testid="input-branch-address" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Código Postal" value={newBranch.postal_code} onChange={(e) => setNewBranch({ ...newBranch, postal_code: e.target.value })} data-testid="input-branch-postal-code" />
                    <Input placeholder="Localidade" value={newBranch.city} onChange={(e) => setNewBranch({ ...newBranch, city: e.target.value })} data-testid="input-branch-city" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="País" value={newBranch.country} onChange={(e) => setNewBranch({ ...newBranch, country: e.target.value })} data-testid="input-branch-country" />
                    <Input placeholder="Coordenadas GPS" value={newBranch.gps_coordinates} onChange={(e) => setNewBranch({ ...newBranch, gps_coordinates: e.target.value })} data-testid="input-branch-gps" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Pessoa de contacto" value={newBranch.contact_person} onChange={(e) => setNewBranch({ ...newBranch, contact_person: e.target.value })} data-testid="input-branch-contact-person" />
                    <Input placeholder="Telefone" value={newBranch.phone} onChange={(e) => setNewBranch({ ...newBranch, phone: e.target.value })} data-testid="input-branch-phone" />
                  </div>
                  <Input placeholder="Email" type="email" value={newBranch.email} onChange={(e) => setNewBranch({ ...newBranch, email: e.target.value })} data-testid="input-branch-email" />
                  <Button type="button" size="sm" className="btn-primary w-full" onClick={addBranch} disabled={savingBranch} data-testid="btn-save-new-branch">
                    {savingBranch ? "A guardar..." : "Guardar Sucursal"}
                  </Button>
                </div>
              )}

              {loadingBranches ? (
                <p className="text-xs text-stone-400">A carregar...</p>
              ) : branches.length === 0 ? (
                <p className="text-xs text-stone-400">Sem sucursais.</p>
              ) : (
                <>
                  {!client.parent_client_id && (
                    <div className="space-y-2 mb-3">
                      <Label htmlFor="cf-primary-branch" className="text-xs">Sucursal principal (aparece selecionada primeiro)</Label>
                      <Select
                        value={formData.primary_branch_id || client.id}
                        onValueChange={(v) => setFormData({ ...formData, primary_branch_id: v === client.id ? "" : v })}
                      >
                        <SelectTrigger id="cf-primary-branch" className="bg-white" data-testid="select-primary-branch">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={client.id}>{client.name} (matriz)</SelectItem>
                          {branches.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}{b.city ? ` · ${b.city}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                  {branches.map((b) => (
                    <div key={b.id} className="p-2 rounded-lg bg-white border border-stone-200" data-testid={`branch-row-${b.id}`}>
                      {editingBranchId === b.id ? (
                        <div className="space-y-2">
                          <Input placeholder="Nome" value={editBranchForm.name} onChange={(e) => setEditBranchForm({ ...editBranchForm, name: e.target.value })} data-testid={`input-edit-branch-name-${b.id}`} />
                          <Input placeholder="Endereço" value={editBranchForm.address} onChange={(e) => setEditBranchForm({ ...editBranchForm, address: e.target.value })} data-testid={`input-edit-branch-address-${b.id}`} />
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Código Postal" value={editBranchForm.postal_code} onChange={(e) => setEditBranchForm({ ...editBranchForm, postal_code: e.target.value })} data-testid={`input-edit-branch-postal-code-${b.id}`} />
                            <Input placeholder="Localidade" value={editBranchForm.city} onChange={(e) => setEditBranchForm({ ...editBranchForm, city: e.target.value })} data-testid={`input-edit-branch-city-${b.id}`} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="País" value={editBranchForm.country} onChange={(e) => setEditBranchForm({ ...editBranchForm, country: e.target.value })} data-testid={`input-edit-branch-country-${b.id}`} />
                            <Input placeholder="Coordenadas GPS" value={editBranchForm.gps_coordinates} onChange={(e) => setEditBranchForm({ ...editBranchForm, gps_coordinates: e.target.value })} data-testid={`input-edit-branch-gps-${b.id}`} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Pessoa de contacto" value={editBranchForm.contact_person} onChange={(e) => setEditBranchForm({ ...editBranchForm, contact_person: e.target.value })} data-testid={`input-edit-branch-contact-person-${b.id}`} />
                            <Input placeholder="Telefone" value={editBranchForm.phone} onChange={(e) => setEditBranchForm({ ...editBranchForm, phone: e.target.value })} data-testid={`input-edit-branch-phone-${b.id}`} />
                          </div>
                          <Input placeholder="Email" type="email" value={editBranchForm.email} onChange={(e) => setEditBranchForm({ ...editBranchForm, email: e.target.value })} data-testid={`input-edit-branch-email-${b.id}`} />
                          <div className="flex gap-2">
                            <Button type="button" size="sm" className="btn-primary" onClick={saveBranchEdit} disabled={savingBranch} data-testid={`btn-save-branch-edit-${b.id}`}>
                              Guardar
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditingBranchId(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">{b.name}</p>
                            {b.city && <p className="text-xs text-stone-400 truncate">{b.city}</p>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button type="button" onClick={() => startEditBranch(b)} className="p-1.5 text-stone-400 hover:text-[#009392] rounded" data-testid={`btn-edit-branch-${b.id}`}>
                              <PencilSimple size={16} />
                            </button>
                            <button type="button" onClick={() => deleteBranch(b.id)} className="p-1.5 text-stone-400 hover:text-red-500 rounded" data-testid={`btn-delete-branch-${b.id}`}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-stone-100 mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel">Cancelar</Button>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-save-client">
              {saving ? "A guardar..." : (client ? "Atualizar" : "Criar")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
